import React, { useState, useEffect } from 'react';
import './App.css';

interface BarkEvent {
  id: number;
  event_type: string;
  ts: number;
  details: string;
}

interface ApiResponse {
  ok: boolean;
  status: {
    busy: boolean;
    eventName: string | null;
    devices: {
      light: string;
      diffuser: string;
    };
    recentEvents: BarkEvent[];
  };
  ts: number;
}

function App() {
  const [status, setStatus] = useState<ApiResponse | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      const response = await fetch('https://zooma-worker.zooma-dev.workers.dev/api/bark');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data: ApiResponse = await response.json();
      setStatus(data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  useEffect(() => {
    // Fetch immediately
    fetchStatus();
    
    // Poll every 1 second for smooth countdown
    const interval = setInterval(fetchStatus, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const formatTimestamp = (ts: number) => {
    return new Date(ts).toLocaleString([], {
      month: 'numeric',
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const timeSince = (ts: number) => {
    const seconds = Math.floor((Date.now() - ts) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const getActivityStatus = () => {
    if (!status?.status.recentEvents || status.status.recentEvents.length === 0) {
      return { emoji: '⚪', text: 'No Action', stateStarted: null, countdown: null };
    }
    
    const latestEvent = status.status.recentEvents[0];
    const now = status.ts; // Use server timestamp instead of client time
    const threeMinutesAgo = now - 3 * 60 * 1000;
    
    if (latestEvent.ts > threeMinutesAgo) {
      // Chilling mode - state started when event occurred, countdown until 3 minutes after
      const timeUntilSwitch = (latestEvent.ts + 3 * 60 * 1000) - now;
      const minutes = Math.floor(timeUntilSwitch / 60000);
      const seconds = Math.floor((timeUntilSwitch % 60000) / 1000);
      const countdown = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      
      return { 
        emoji: '😌', 
        text: 'Chilling', 
        stateStarted: new Date(latestEvent.ts), 
        countdown 
      };
    } else {
      // No Action mode - state started 3 minutes after the last event
      const stateStarted = new Date(latestEvent.ts + 3 * 60 * 1000);
      return { 
        emoji: '⚪', 
        text: 'No Action', 
        stateStarted, 
        countdown: null 
      };
    }
  };

  const formatStateTime = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="app">
      {/* Sticky Top Panel - 25% of viewport height */}
      <div className="status-panel">
        <div className="activity-status">
          {(() => {
            const activityStatus = getActivityStatus();
            return (
              <>
                <div className="activity-emoji">{activityStatus.emoji}</div>
                <div className="activity-text">{activityStatus.text}</div>
              </>
            );
          })()}
        </div>
        <div className="state-info">
          {(() => {
            const activityStatus = getActivityStatus();
            return (
              <div className="state-timing">
                <span>Since {formatStateTime(activityStatus.stateStarted)}</span>
                {activityStatus.countdown && (
                  <span className="countdown">⏱️ {activityStatus.countdown}</span>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Scrollable Events List - 75% of viewport height */}
      <div className="events-list">
        {status?.status.recentEvents && status.status.recentEvents.length > 0 ? (
          <div className="events">
            {status.status.recentEvents.map((event) => (
              <div key={event.id} className="event-item">
                <div className="event-title">From No Action to Calming</div>
                <div className="event-header">
                  <span className="event-time">{timeSince(event.ts)}</span>
                </div>
                <div className="event-timestamp">
                  {formatTimestamp(event.ts)}
                </div>
                {event.details && (
                  <div className="event-details">
                    {JSON.stringify(JSON.parse(event.details), null, 2)}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-events">No recent events</div>
        )}
      </div>
    </div>
  );
}

export default App;
